// packages/ui-kit/src/people/AvatarOrInitials.tsx
// C3F v18 — reference: design/system/components/people/AvatarOrInitials.{jsx,prompt.md}
import { useMemo } from "react";

/**
 * The avatar is MONOCHROME on purpose. A per-person color would be decorative
 * color, and in this system color is rationed: Spectrum is the brand signature,
 * the four state colors are states. A random purple circle next to an accent
 * CTA reads as an alignment mistake, not an identity. People are told apart by
 * their initials — or their photo, when there is one. If a per-person color is
 * ever truly needed, it must be an identity scale OUTSIDE Spectrum and outside
 * the states, decided at brand level — not inside this component.
 */

type Tone = "default" | "strong" | "onDark";

interface Props {
  displayName: string;
  // explicit `| undefined` to satisfy exactOptionalPropertyTypes when callers
  // pass `photoUrl={maybeUndefined}` instead of conditionally spreading.
  photoUrl?: string | undefined;
  size?: number;
  /** `onDark` for surfaces fixed to Night in BOTH themes (sidebar, rail,
   *  bottom bar): the default `bg-sunken` follows the theme and would vanish
   *  there. `strong` when maximum weight is needed (single avatar in a header). */
  tone?: Tone;
  className?: string;
}

const toneCls: Record<Tone, string> = {
  default: "bg-sunken text-fg-default border border-border-default",
  // An inversion INSIDE a theme-following surface uses theme tokens
  // (bg-inverse/fg-inverse swap by themselves), never the fixed Night/Paper pair.
  strong: "bg-inverse text-fg-inverse border border-transparent",
  // Paper-alpha veil hardcoded by the DS reference itself for always-Night surfaces.
  onDark: "bg-[rgba(244,240,233,0.12)] text-praesto-paper border border-[rgba(244,240,233,0.18)]",
};

/**
 * The closed C3F v18 type scale, small end (design/system/tokens).
 *
 * The DS reference derives the glyph size from the diameter — `round(size *
 * 0.36)` — which is a scaling heuristic, while the closed scale is a hard
 * brand rule. They disagree at small diameters: the 24px avatar `PersonLink`
 * uses in the dense shifts grid lands on 8.64 → 9px, two steps below the
 * scale floor, and the v18 visual walkthrough flags it on `home-today` and
 * `shifts-week`. The reference simply never contemplated a 24px avatar (its
 * documented sizes are 32/36/44, default 40).
 *
 * Where the two rules conflict the scale wins, so the derived value is snapped
 * to the nearest scale step instead of being used raw. This preserves every
 * documented size (32→12, 36→13, 40→14, 44→16 are all unchanged) and only
 * moves the two that were off-scale: 24 → 11 (bigger, and more legible) and
 * 48 → 16.
 */
const GLYPH_SCALE_PX = [11, 12, 13, 14, 16, 18, 19, 24] as const;

function snapToScale(px: number): number {
  return GLYPH_SCALE_PX.reduce((best, step) =>
    Math.abs(step - px) < Math.abs(best - px) ? step : best,
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function AvatarOrInitials({
  displayName,
  photoUrl,
  size = 32,
  tone = "default",
  className = "",
}: Props) {
  const initialsText = useMemo(() => initials(displayName), [displayName]);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={displayName}
        width={size}
        height={size}
        className={`shrink-0 rounded-full object-cover ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={displayName}
      className={`box-border grid shrink-0 select-none place-items-center rounded-full font-sans font-semibold leading-none tracking-[-0.02em] ${toneCls[tone]} ${className}`}
      // Truly dynamic values only: the DS reference derives the glyph size from
      // the diameter (round(size * 0.36)), snapped to the closed type scale.
      style={{ width: size, height: size, fontSize: snapToScale(Math.round(size * 0.36)) }}
    >
      {initialsText}
    </span>
  );
}
